import { GenerateThemeTranslationVoiceoversDialog } from '@/components/attractions/themes/translations/GenerateThemeTranslationVoiceoversDialog';
import { GenerateVoiceoversDialog as GenerateThemeVoiceoversDialog } from '@/components/attractions/themes/GenerateVoiceoversDialog';
import { GenerateTranslationVoiceoversDialog } from '@/components/attractions/translations/GenerateTranslationVoiceoversDialog';
import type { HealthRow } from '@/hooks/useHealthCheck';
import { GenerateVoiceoversAction } from '@/pages/attractions/actions';
import { Button } from '@startupquickstart/frontend';
import { AudioLines } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

type VoiceoverType = 'both' | 'description' | 'long_description';

interface RegenerateVoiceoverRowButtonProps {
  row: HealthRow;
}

/**
 * Per-row "Sync" action surfaced in the Descriptions & Voiceovers health
 * view. Opens the matching pre-existing generate-voiceovers dialog based
 * on the row's kind so the regenerate behaviour is identical to the
 * dialogs already wired on detail pages (hash refresh on server, hand-off
 * to background, global completion toast).
 *
 * Coverage:
 *  - `attraction` → GenerateVoiceoversAction
 *  - `theme` → GenerateThemeVoiceoversDialog
 *  - `attraction-translation` → GenerateTranslationVoiceoversDialog
 *  - `theme-translation` → GenerateThemeTranslationVoiceoversDialog
 *  - `tour` / `tour-translation` → no inline dialog yet (deep-link from
 *    the row label takes the user to where they can regenerate).
 *
 * The button infers `voiceoverType` from the row's issue fields so the
 * confirm dialog defaults to the right scope (short, long, or both).
 */
export function RegenerateVoiceoverRowButton({
  row,
}: RegenerateVoiceoverRowButtonProps): JSX.Element | null {
  const [open, setOpen] = useState(false);

  const defaultVoiceoverType = inferVoiceoverType(row);
  const dialog = renderDialogForRow(row, open, setOpen, defaultVoiceoverType);
  if (!dialog) return null;

  return (
    <>
      <Button size="sm" variant="default" onClick={() => setOpen(true)}>
        <AudioLines className="h-3.5 w-3.5" />
        Sync
      </Button>
      {dialog}
    </>
  );
}

function renderDialogForRow(
  row: HealthRow,
  open: boolean,
  setOpen: (next: boolean) => void,
  defaultVoiceoverType: VoiceoverType,
): JSX.Element | null {
  // For non-missing-translation rows we need IDs to drive the per-kind
  // generate-voiceover dialog. `row.context` is the authoritative source,
  // but fall back to `parentId` / `row.id` so the button still works if
  // an older API response (pre-context-field) is in flight.
  const attractionId =
    row.context?.attractionId ??
    (row.kind === 'attraction'
      ? row.id
      : row.kind === 'theme' || row.kind === 'attraction-translation'
        ? row.parentId
        : undefined);
  const themeId =
    row.context?.themeId ??
    (row.kind === 'theme'
      ? row.id
      : row.kind === 'theme-translation'
        ? row.parentId
        : undefined);

  if (row.kind === 'attraction') {
    return (
      <GenerateVoiceoversAction
        attractionId={row.id}
        open={open}
        onOpenChange={setOpen}
        defaultVoiceoverType={defaultVoiceoverType}
      />
    );
  }

  if (row.kind === 'theme' && attractionId && themeId) {
    return (
      <GenerateThemeVoiceoversDialog
        attractionId={attractionId}
        themeId={themeId}
        open={open}
        onClose={() => setOpen(false)}
      />
    );
  }

  if (row.kind === 'attraction-translation' && attractionId) {
    return (
      <GenerateTranslationVoiceoversDialog
        attractionId={attractionId}
        translationId={row.id}
        open={open}
        onOpenChange={setOpen}
      />
    );
  }

  // theme-translation genuinely needs both IDs and one of them
  // (attractionId) is only available via row.context — no fallback.
  if (row.kind === 'theme-translation' && attractionId && themeId) {
    return (
      <GenerateThemeTranslationVoiceoversDialog
        attractionId={attractionId}
        themeId={themeId}
        translationId={row.id}
        open={open}
        onClose={() => setOpen(false)}
      />
    );
  }

  return null;
}

function inferVoiceoverType(row: HealthRow): VoiceoverType {
  const fields = row.issues
    .filter((issue) => issue.type === 'missing' || issue.type === 'out-of-sync')
    .map((issue) => issue.field);
  const hasShort = fields.includes('voiceover') || fields.includes('description');
  const hasLong =
    fields.includes('longVoiceover') || fields.includes('longDescription');
  if (hasShort && hasLong) return 'both';
  if (hasLong) return 'long_description';
  return 'description';
}
